import { Component, DestroyRef, OnDestroy, OnInit, inject } from "@angular/core";
import { BasemapService } from "../basemap.service";
import { LoadingService } from "../../loading/loading.service";
import { Coordinate, ObjectCoordinate } from "../../../shared/contract/Address/Coordinates";
import { EPSGType } from "../../../shared/contract/Enum/EPSG";
import { DataModeService } from "../../menu/data-mode.service";
import { LayerService } from "../../layer/layer.service";
import { PermissionService } from "src/app/modules/core/services/permission.service";
import { SpatialSelectionService } from "src/app/modules/report/spatial-selection.service";
import { InitService } from "src/app/shared/services/init.service";
import { PopupService } from "../../popup/popup.service";
import { NotificationService } from "@services/notification.service";
import { catchError, map, filter, Subscription, tap, switchMap, finalize, EMPTY } from "rxjs";
import { AddressSearchApiService } from "@services/address-search-api.service";
import { HeaderLocationService } from "src/app/shared/services/header-location.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MODULE_NAMES } from "src/app/shared/consts/icon-features";
import { Address } from "src/app/shared/contract/Address/Address";
import { getAddressTitle } from "src/app/shared/helpers/address-helper";
import { AddressesNearByRequest } from "src/app/shared/contract/Address/AddressesNearByRequest";
import { DisableRightClickDirective } from "@directives/disable-right-click.directive";
import { RealEstateStoreService } from "../../real-estate/services/real-estate-store.service";
import { LandingPageDialogComponent } from "../../reception/landing-page-dialog/landing-page-dialog.component";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "app-basemap",
  templateUrl: "./basemap.component.html",
  styleUrls: ["./basemap.component.scss"],
  imports: [DisableRightClickDirective]
})
export class BasemapComponent implements OnInit, OnDestroy {
  baseMapService = inject(BasemapService);
  spatialSelectionService = inject(SpatialSelectionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingService = inject(LoadingService);
  private readonly permissionService = inject(PermissionService);
  private readonly dataModeService = inject(DataModeService);
  private readonly layerService = inject(LayerService);
  private readonly initService = inject(InitService);
  private readonly popupService = inject(PopupService);
  private readonly notificationService = inject(NotificationService);
  private readonly addressSearchService = inject(AddressSearchApiService);
  private readonly headerLocationService = inject(HeaderLocationService);
  private readonly realEstateStoreService = inject(RealEstateStoreService);

  private readonly matDialog = inject(MatDialog);

  private countryRightSubscription: Subscription = null;

  ngOnInit() {
    this.baseMapService.initMap();
    this.popupService.initializeMapClickHandler();
    this.layerService.setBaseLayer();
  }

  ngOnDestroy() {
    this.popupService.removeMapClickHandler();
    this.baseMapService.removePointerDragHandler();
  }

  protected onDoubleClick(event: MouseEvent) {
    const selectedMode = this.dataModeService.selectedDataMode();
    if (selectedMode?.Category === MODULE_NAMES.SpatialAnalysis || selectedMode?.Module === MODULE_NAMES.Portfolio) {
      return;
    }

    if (this.permissionService.isBlockedByQuota()) {
      this.matDialog.open(LandingPageDialogComponent);
      return;
    }

    const coordinate = new Coordinate(this.baseMapService.map.getEventCoordinate(event), EPSGType.OSM);
    const countryRightsModuleIds = [MODULE_NAMES.CommercialRealEstate, MODULE_NAMES.ResidentialRealEstate, MODULE_NAMES.Rvm];
    const moduleIds = countryRightsModuleIds
      .map(moduleName => this.initService.InitData.ModuleRight.find(e => e.Name === moduleName)?.Id)
      .filter(id => id !== undefined);

    this.loadingService.show();
    this.baseMapService.currentSelectedMarkerCoordinate = coordinate;
    this.countryRightSubscription?.unsubscribe();

    this.countryRightSubscription = this.permissionService
      .getCountryByCoordinates({
        Latitude: coordinate.Get(EPSGType.LonLat).Y,
        Longitude: coordinate.Get(EPSGType.LonLat).X
      })
      .pipe(
        filter(country => {
          if (!country) {
            this.notificationService.failure("Exception.Failure.Status.4");
          }

          return !!country;
        }),
        switchMap(country => {
          return this.permissionService
            .hasRightForAnyModules({
              Coordinate: {
                Country: country,
                Latitude: coordinate.Get(EPSGType.LonLat).Y,
                Longitude: coordinate.Get(EPSGType.LonLat).X
              } as ObjectCoordinate,
              ModuleIds: moduleIds
            })
            .pipe(map(userHasPermission => ({ country, userHasPermission })));
        }),
        switchMap(request => {
          if (!request.userHasPermission) {
            this.notificationService.failure("Exception.Failure.Status.4");
            return EMPTY;
          }

          this.baseMapService.country.next(request.country);

          if (this.baseMapService.getZoomLevel() < 15) {
            this.showZoomLevelInfo();
          }

          if (this.canSearchNearbyAddresses()) {
            return this.handleNearbyAddressSearch(coordinate, request.country);
          }

          if (this.isCommercialRealEstateOutsideGermany(selectedMode, request.country)) {
            this.notificationService.failure(`Exception.Failure.${selectedMode.Module}.State`);
          }

          return this.handleObjectRequest(coordinate, request.country, selectedMode);
        }),
        catchError(() => {
          console.error("Error on loading the country");
          this.showCountryRightInfo();
          return EMPTY;
        }),
        finalize(() => this.loadingService.hide()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Remove popups on right click
  protected onRightClick = () => {
    this.popupService.closeActivePopup();
  };

  private handleNearbyAddressSearch(coordinate: Coordinate, country: string) {
    return this.fetchNearbyAddresses(coordinate, country).pipe(
      map(proposedAddresses => (proposedAddresses?.length > 0 ? this.mapAddressToSuggestionAddress(proposedAddresses) : [])),
      tap(suggestions => this.headerLocationService.handleAddressOnMapClick(coordinate, suggestions, country))
    );
  }

  private handleObjectRequest(coordinate: Coordinate, country: string, selectedMode: any) {
    this.realEstateStoreService.changedValueFixing.set(false);

    // Initialize loading screen
    this.loadingService.show();

    if (selectedMode?.Module === MODULE_NAMES.ResidentialRealEstate || selectedMode?.Module === MODULE_NAMES.CommercialRealEstate) {
      return this.fetchNearbyAddresses(coordinate, country).pipe(
        filter(proposedAddresses => proposedAddresses?.length > 0),
        tap(proposedAddresses => {
          if (selectedMode?.Module === MODULE_NAMES.ResidentialRealEstate) {
            const suggestions = proposedAddresses.map(address => ({
              Description: getAddressTitle(address) || address.DisplayAddress,
              Address: address
            }));

            this.headerLocationService.handleAddressOnMapClick(coordinate, suggestions, country);
          }
        })
      );
    }
  }

  private fetchNearbyAddresses(coordinate: Coordinate, country: string) {
    return this.addressSearchService.getAddressesNearBy({
      IsGlobalSearch: this.headerLocationService.isGlobalSearchMode,
      Coord: {
        Country: country,
        Latitude: coordinate.Get(EPSGType.LonLat).Y,
        Longitude: coordinate.Get(EPSGType.LonLat).X
      },
      WebMercator: {
        X: coordinate.Get(EPSGType.OSM).X,
        Y: coordinate.Get(EPSGType.OSM).Y
      }
    } as AddressesNearByRequest);
  }

  private isCommercialRealEstateOutsideGermany(selectedMode: any, country: string) {
    return (
      (selectedMode?.Module === MODULE_NAMES.CommercialRealEstate && country !== "de") ||
      (this.dataModeService.allowedDataModes().some(e => e.Selected && e.Module === MODULE_NAMES.CommercialRealEstate) && country !== "de")
    );
  }

  private canSearchNearbyAddresses() {
    const allowedModules = [
      MODULE_NAMES.ResidentialRealEstate,
      MODULE_NAMES.CommercialRealEstate,
      MODULE_NAMES.BuildingLand,
      MODULE_NAMES.Rvm
    ];
    const allowedCategories = ["SpatialAnalysis"];

    return (
      this.dataModeService.selectedDataMode() === null ||
      !allowedModules.includes(this.dataModeService.selectedDataMode()?.Module as (typeof allowedModules)[number]) ||
      !allowedCategories.includes(this.dataModeService.selectedDataMode()?.Category)
    );
  }

  private mapAddressToSuggestionAddress(addresses: Address[]) {
    return addresses.map(address => ({
      Description: getAddressTitle(address) || address.DisplayAddress,
      Address: address
    }));
  }

  private showCountryRightInfo() {
    this.notificationService.info("Exception.Info.Accumate");
  }

  private showZoomLevelInfo() {
    this.notificationService.info("Exception.Info.Zoom");
  }
}
